import { useEffect, useId, useRef, useState } from 'react';

export default function Modal({ open, onClose, title, eyebrow, children, footer, size = 'medium' }) {
  const ref = useRef(null);
  const timerRef = useRef(null);
  const [closing, setClosing] = useState(false);
  const titleId = useId();

  // `open` is authoritative in both directions. Closing used to happen only in
  // requestClose(), which is reached from the ×, the backdrop and Esc — so a
  // caller that closed the dialog programmatically after an action succeeded
  // (setting open back to false) left the native <dialog> on screen, because
  // nothing had called .close() on it.
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return undefined;
    if (open) {
      if (!dialog.open) dialog.showModal();
    } else if (dialog.open) {
      if (timerRef.current) window.clearTimeout(timerRef.current);
      setClosing(false);
      dialog.close();
    }
    return undefined;
  }, [open]);

  // Separate from the open/close effect so an in-flight close animation is not
  // cancelled every time `open` changes.
  useEffect(() => () => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
  }, []);

  function requestClose() {
    if (closing) return;
    setClosing(true);
    timerRef.current = window.setTimeout(() => {
      ref.current?.close();
      setClosing(false);
      onClose();
    }, 170);
  }

  return (
    <dialog
      ref={ref}
      className={`studio-modal studio-modal--${size}${closing ? ' is-closing' : ''}`}
      aria-labelledby={titleId}
      onCancel={(event) => {
        event.preventDefault();
        requestClose();
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) requestClose();
      }}
    >
      <div className="studio-modal__surface">
        <header className="studio-modal__head">
          <div>
            {eyebrow ? <p className="cap">{eyebrow}</p> : null}
            <h2 id={titleId}>{title}</h2>
          </div>
          <button type="button" className="studio-modal__close" onClick={requestClose} aria-label="Close dialog">×</button>
        </header>
        <div className="studio-modal__body">{children}</div>
        {footer ? <footer className="studio-modal__foot">{footer}</footer> : null}
      </div>
    </dialog>
  );
}
